/** @forge-header
 * @id          nhlParsers
 * @role        NHL ticker parsers | game moneyline | player-prop vocabulary
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo | kalshiTicker/helpers:nhlTeamFromCode,parseFallback | kalshiTicker/teamNames:splitNhlTeams
 * @downstream  kalshiTicker/parse
 * @exports     parseNhlGame:fn, NHL_PROP_SERIES, classifyNhlExtendedTicker:fn, NhlPropStat:type, NHL_WIRE_PROP_STAT, NHL_PROP_STATS, NHL_PROP_STAT_LABELS, NHL_PROP_BINARY_THRESHOLD, NHL_PROP_BINARY_WORDING
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo } from '../types';
import { parseDateFromTicker } from '../types';
import { parseFallback, nhlTeamFromCode } from '../helpers';
import { NHL_TEAM_CODES, splitNhlTeams } from '../teamNames';

/** NHL game moneyline: KXNHLGAME-26FEB08SEATOR-SEA */
export function parseNhlGame(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  // Date is 7 chars; codes are at least 2+2 (TB, SJ, NJ, LA) → mid ≥ 11.
  if (mid.length < 11) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const split = splitNhlTeams(mid.slice(7));
  if (!split) {
    return parseFallback(raw);
  }

  const [awayCode, homeCode] = split;
  const teamCode = parts[2] ?? null;

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: nhlTeamFromCode(awayCode) ?? undefined,
    homeName: nhlTeamFromCode(homeCode) ?? undefined,
  };

  const shortLabel = `NHL: ${awayCode} @ ${homeCode}`;
  const fullLabel = teamCode
    ? `NHL: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode} · ${teamCode} Win`
    : `NHL: ${game.awayName ?? awayCode} @ ${game.homeName ?? homeCode}`;

  return {
    raw,
    category: 'nhl-game',
    series: 'KXNHLGAME',
    date,
    game,
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome: teamCode,
    shortLabel,
    fullLabel,
  };
}

// ── NHL player props (#1218, NHLALT since 2026-10-16) ──────────────────────
//
// Selected and named by the WIRE (`class: 'player-prop'` + `stat`, with
// `player` / `playerTeam` / `playerKey` stamped by core). The ticker classifier
// below is the NFL-pattern fallback: what a held position or a deep link knows
// before its frame arrives, and the identity when a frame lacks the fields.
// The g1 ids are namespaced (`nhl-…`) because the wire spells hockey points
// and assists `pts` / `ast` — the NBA spellings — and `PropStat` ids must stay
// disjoint across sports: a synth key carries the stat id with no sport tag.

export type NhlPropStat =
  | 'nhl-goals' | 'nhl-anytime-goal' | 'nhl-ast' | 'nhl-pts' | 'nhl-saves' | 'nhl-first-goal';

/** Wire `stat` (hydracore HYDRA_API.md § Player props, NHL vocabulary) → g1 id.
 *  A stat outside this table is ignored, never guessed (rule 8). */
export const NHL_WIRE_PROP_STAT: Readonly<Record<string, NhlPropStat>> = {
  goals: 'nhl-goals',
  'anytime-goal': 'nhl-anytime-goal',
  ast: 'nhl-ast',
  pts: 'nhl-pts',
  saves: 'nhl-saves',
  'first-goal': 'nhl-first-goal',
};

/** Stat chip order. */
export const NHL_PROP_STATS: readonly NhlPropStat[] = [
  'nhl-goals', 'nhl-anytime-goal', 'nhl-ast', 'nhl-pts', 'nhl-saves', 'nhl-first-goal',
];

export const NHL_PROP_STAT_LABELS: Record<NhlPropStat, string> = {
  'nhl-goals': 'Goals',
  'nhl-anytime-goal': 'Anytime Goal',
  'nhl-ast': 'Assists',
  'nhl-pts': 'Points',
  'nhl-saves': 'Saves',
  'nhl-first-goal': 'First Goal',
};

/** The yes/no families carry no strike; like the NFL touchdown binaries each
 *  gets a FIXED identity threshold (its own "N+" reading) so the numeric
 *  `PropRung` / synth-key contract holds. The wording never prints it. */
export const NHL_PROP_BINARY_THRESHOLD: Partial<Record<NhlPropStat, number>> = {
  'nhl-anytime-goal': 1,
  'nhl-first-goal': 1,
};

export function isNhlBinaryPropStat(stat: NhlPropStat): boolean {
  return NHL_PROP_BINARY_THRESHOLD[stat] !== undefined;
}

/** The two sides of each yes/no family, worded after the player's name. The
 *  ladders take the shared "N+ Stat" wording (lib/sport/propStat). */
export const NHL_PROP_BINARY_WORDING: Partial<Record<NhlPropStat, { yes: string; no: string }>> = {
  'nhl-first-goal': { yes: 'scores 1st goal', no: 'does not score 1st goal' },
  'nhl-anytime-goal': { yes: 'scores a goal', no: 'does not score a goal' },
};

/** Series ticker → stat, keyed by the EXACT series segment (before the first
 *  dash) — a startsWith match would let KXNHLSAVE eat KXNHLSAVES and
 *  KXNHLGOAL eat KXNHLGOALIE…. Both SAVE spellings are catalogued as `saves`. */
export const NHL_PROP_SERIES: Readonly<Record<string, NhlPropStat>> = {
  KXNHLGOAL: 'nhl-goals',
  KXNHLANYGOAL: 'nhl-anytime-goal',
  KXNHLAST: 'nhl-ast',
  KXNHLPTS: 'nhl-pts',
  KXNHLSAVE: 'nhl-saves',
  KXNHLSAVES: 'nhl-saves',
  KXNHLFIRSTGOAL: 'nhl-first-goal',
};

/** Structural classification of one NHL player-prop market ticker. The live
 *  frame's wire fields (`stat`, `player`, `playerTeam`, `playerKey`) stay the
 *  authority wherever a frame exists; this is what a held position or a deep
 *  link knows BEFORE its frame arrives — "is this a prop, which stat, whose". */
export interface NhlExtendedClass {
  kind: 'prop';
  stat: NhlPropStat;
  date: string;
  /** Null when the packed pair does not resolve on the registry. */
  awayCode: string | null;
  homeCode: string | null;
  teamCode: string;
  /** `<INITIAL><SURNAME>` from the ticker. */
  playerId: string;
  jerseyNumber?: number;
  /** Yes-ladder rung N (YES at N or more; floorStrike N − 0.5). Null on the
   *  yes/no families (anytime / first goal), which carry no strike segment. */
  threshold: number | null;
}

/** Split `<TEAM><REST>` on a KNOWN NHL code: the event pair first (longest
 *  first — the player's team is one of the two by construction), then the
 *  registry, 3 letters before 2 (LA / SJ / TB / NJ). Never a fixed slice. */
function splitKnownNhlTeamPrefix(blob: string, pair: readonly [string, string] | null): [string, string] | null {
  const candidates = pair ? [...pair].sort((a, b) => b.length - a.length) : [];
  for (const code of candidates) {
    if (blob.startsWith(code) && blob.length > code.length) return [code, blob.slice(code.length)];
  }
  for (const len of [3, 2]) {
    const code = blob.slice(0, len);
    if (code.length === len && NHL_TEAM_CODES.has(code) && blob.length > len) return [code, blob.slice(len)];
  }
  return null;
}

/**
 * Classify an NHL player-prop ticker by its EXACT series segment:
 * `<series>-<core>-<TEAM><INITIAL><SURNAME><JERSEY>-<N>` on a ladder,
 * `<series>-<core>-<TEAM><INITIAL><SURNAME><JERSEY>` on a yes/no. Null for
 * anything else or any segment that does not match (fail closed).
 */
export function classifyNhlExtendedTicker(ticker: string): NhlExtendedClass | null {
  const parts = ticker.trim().toUpperCase().split('-');
  const stat = NHL_PROP_SERIES[parts[0] ?? ''];
  if (!stat) return null;
  const core = parts[1] ?? '';
  if (core.length < 11) return null; // 7 date chars + the shortest pair (LASJ)
  const date = parseDateFromTicker(core.slice(0, 7));
  if (!date) return null;
  const pair = splitNhlTeams(core.slice(7));
  const binary = isNhlBinaryPropStat(stat);
  if (parts.length !== (binary ? 3 : 4)) return null;
  const split = splitKnownNhlTeamPrefix(parts[2] ?? '', pair);
  const m = split?.[1].match(/^([A-Z]+)(\d+)?$/);
  if (!split || !m) return null;
  let threshold: number | null = null;
  if (!binary) {
    if (!/^\d+$/.test(parts[3] ?? '')) return null;
    threshold = Number(parts[3]);
  }
  return {
    kind: 'prop',
    stat,
    date,
    awayCode: pair?.[0] ?? null,
    homeCode: pair?.[1] ?? null,
    teamCode: split[0],
    playerId: m[1],
    jerseyNumber: m[2] ? Number(m[2]) : undefined,
    threshold,
  };
}
